import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { API_CONFIG } from '../config/api';
import Modal from '../components/Modal';
import { EyeIcon } from '../components/Icons';
import { toast } from 'react-toastify';
import { formatDate } from '../utils/dateFormatter';

interface SubscriberDetail {
  id: string;
  userId: string;
  name: string;
  email: string;
  phone?: string;
  profile?: {
    firstName?: string;
    lastName?: string;
    preferredName?: string;
  };
  plan: string;
  hasSubscription: boolean;
  subscriptionId?: string;
  startDate?: string;
  endDate?: string;
  status: 'active' | 'expired' | 'cancelled' | 'free';
  createdAt: string;
  lastLogin?: string;
  isActive: boolean;
  role: string;
  paymentMethod?: {
    type: string;
    last4?: string;
  };
}

interface SubscriptionDetail {
  id: string;
  plan: string;
  price: number;
  billingCycle: string;
  status: string;
  nextPayment?: string;
  paymentMethod?: {
    type: string;
    last4?: string;
  };
  totalBilled: number;
}

interface HistoryItem {
  type: string;
  description: string;
  date: string;
  icon: string;
}

interface Plan {
  _id: string;
  name: string;
  description: string;
  unit_name: string;
  token_rate: number;
  message_rate: number;
  currency: string;
  billing_interval: string;
  free_tokens_limit: number;
  free_messages_limit: number;
  price: number;
  sampleCosts: {
    lowUsage: number;
    mediumUsage: number;
    highUsage: number;
  };
  metadata?: {
    features?: string[];
  };
}


const SubscriberDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const { user: currentUser, updateUser } = useAuth();

  const [subscriber, setSubscriber] = useState<SubscriberDetail | null>(null);
  const [subscription, setSubscription] = useState<SubscriptionDetail | null>(null);
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Password reset modal state
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [resetPasswordForm, setResetPasswordForm] = useState({
    password: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    password: false,
    confirmPassword: false
  });
  const [resetPasswordLoading, setResetPasswordLoading] = useState(false);


  // Change role modal state
  const [showChangeRoleModal, setShowChangeRoleModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState<'User' | 'Consultant' | 'Admin' | 'Enterprise'>('User');
  const [changeRoleLoading, setChangeRoleLoading] = useState(false);

  // Change plan modal state
  const [showChangePlanModal, setShowChangePlanModal] = useState(false);
  const [availablePlans, setAvailablePlans] = useState<Plan[]>([]);
  const [changePlanLoading, setChangePlanLoading] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);

  // Remove subscription modal state
  const [showRemoveSubscriptionModal, setShowRemoveSubscriptionModal] = useState(false);
  const [removeSubscriptionLoading, setRemoveSubscriptionLoading] = useState(false);

  // Add days modal state
  const [showAddDaysModal, setShowAddDaysModal] = useState(false);
  const [addDaysForm, setAddDaysForm] = useState({
    days: '',
    planIdForFree: '',
    reason: ''
  });
  const [addDaysLoading, setAddDaysLoading] = useState(false);

  // Fetch subscriber details
  useEffect(() => {
    const fetchSubscriberDetails = async () => {
      if (!id) {
        setError('No subscriber ID provided');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);

        const response = await fetch(`${API_CONFIG.ENDPOINTS.USERS_ADMIN_DETAIL}/${id}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          if (response.status === 404) {
            throw new Error('Subscriber not found');
          } else if (response.status === 403) {
            throw new Error('Access denied. Admin privileges required.');
          } else {
            throw new Error('Failed to fetch subscriber details');
          }
        }

        const fullResponse = await response.json();

        if (fullResponse.success && fullResponse.data) {
          const { user, subscription, history } = fullResponse.data;
          setSubscriber(user);
          setSubscription(subscription);
          setHistory(history || []);
        } else {
          throw new Error('Invalid response format');
        }

      } catch (err) {

        setError(err instanceof Error ? err.message : 'Failed to load subscriber details');
      } finally {
        setIsLoading(false);
      }
    };

    fetchSubscriberDetails();
  }, [id]);


  // Format currency helper
  const formatCurrency = (amount: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  // Generate features array from plan data
  const generatePlanFeatures = (plan: Plan): string[] => {
    const features: string[] = [];

    // Add metadata features first (if available)
    if (plan.metadata?.features && Array.isArray(plan.metadata.features)) {
      features.push(...plan.metadata.features);
    }

    // Add real plan limits as features
    if (typeof plan.free_messages_limit === 'number' && plan.free_messages_limit >= 0) {
      features.push(`Up to ${plan.free_messages_limit.toLocaleString()} messages/month`);
    }

    if (typeof plan.free_tokens_limit === 'number' && plan.free_tokens_limit > 0) {
      features.push(`Up to ${plan.free_tokens_limit.toLocaleString()} tokens/month`);
    }

    // If no features at all, add a basic message
    if (features.length === 0) {
      features.push('Basic access to all coaches');
    }

    return features;
  };

  // Format billing interval
  const formatBillingInterval = (interval: string) => {
    if (!interval) return 'month';
    return interval === 'month' ? 'month' : interval === 'year' ? 'year' : interval;
  };

  // Handle breadcrumb navigation
  const handleBackToSubscribers = () => {
    navigate('/panel/admin/subscriber-management');
  };

  // Fetch available plans for change plan modal
  const fetchAvailablePlans = async () => {
    try {
      const response = await fetch(API_CONFIG.ENDPOINTS.BILLING_PLANS_ADMIN, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch plans');
      }

      const data = await response.json();
      // Default plan types to show (matching SubscriptionPlansPage default view)
      const defaultPlanTypes = ['free', 'basic', 'premium', 'pro', 'consultant', 'enterprise'];
      // Filter for plans that match default types (same as SubscriptionPlansPage default)
      const filteredPlans = (data.data || [])
        .filter((plan: any) => 
          defaultPlanTypes.some(type =>
            plan.name.toLowerCase().includes(type.toLowerCase())
          )
        );
      setAvailablePlans(filteredPlans);
      
      // Auto-select the current plan
      if (subscription) {
        const currentPlanId = filteredPlans.find((plan: any) => plan.name === subscription.plan)?._id;
        if (currentPlanId) {
          setSelectedPlanId(currentPlanId);
        }
      }
    } catch (error) {

      toast.error('Failed to load available plans');
    }
  };

  // Handle delete account
  const handleDeleteAccount = async () => {
    if (!subscriber) return;

    try {
      setIsLoading(true);
      setError(null);

      const response = await fetch(`${API_CONFIG.ENDPOINTS.USERS_ADMIN_DETAIL}/${subscriber.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.message || 'Failed to delete account');
      }

      // Navigate back to subscriber management page
      navigate('/panel/admin/subscriber-management');
    } catch (err) {

      setError(err instanceof Error ? err.message : 'An error occurred');
      setShowDeleteModal(false);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle reset password
  const handleResetPassword = async () => {
    if (!subscriber) return;

    // Validate password match
    if (resetPasswordForm.password !== resetPasswordForm.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    // Basic password validation
    if (resetPasswordForm.password.length < 8) {
      toast.error('Password must be at least 8 characters long');
      return;
    }

    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/;
    if (!passwordRegex.test(resetPasswordForm.password)) {
      toast.error('Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character');
      return;
    }

    try {
      setResetPasswordLoading(true);

      const response = await fetch(`${API_CONFIG.ENDPOINTS.USERS_ADMIN_RESET_PASSWORD}/${subscriber.id}/reset-password`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          password: resetPasswordForm.password,
          confirmPassword: resetPasswordForm.confirmPassword
        })
      });

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.message || 'Failed to reset password');
      }

      toast.success('Password reset successfully');
      setShowResetPasswordModal(false);
      setResetPasswordForm({ password: '', confirmPassword: '' });
      setShowPasswords({ password: false, confirmPassword: false });

    } catch (err) {

      toast.error(err instanceof Error ? err.message : 'Failed to reset password');
    } finally {
      setResetPasswordLoading(false);
    }
  };


  // Handle change role
  const handleChangeRole = async () => {
    if (!subscriber) return;

    try {
      setChangeRoleLoading(true);

      const response = await fetch(`${API_CONFIG.ENDPOINTS.USERS_ADMIN_UPDATE_ROLE}/${subscriber.id}/role`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ role: selectedRole })
      });

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.message || 'Failed to change role');
      }

      // Refresh subscriber data BEFORE showing success message
      if (id) {
        try {
          const refreshResponse = await fetch(`${API_CONFIG.ENDPOINTS.USERS_ADMIN_DETAIL}/${id}`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json'
            }
          });

          if (refreshResponse.ok) {
            const fullResponse = await refreshResponse.json();

            if (fullResponse.success && fullResponse.data) {
              const { user, subscription, history } = fullResponse.data;
              setSubscriber(user);
              setSubscription(subscription);
              setHistory(history || []);

              // If the updated user is the current logged-in user, refresh their auth context
              if (currentUser && currentUser.id === user.id) {
                updateUser({
                  ...currentUser,
                  role: selectedRole
                });
              }
            }
          }
        } catch (error) {
          console.error('Error refreshing subscriber data:', error);
        }
      }

      toast.success(`User role changed to ${selectedRole.charAt(0).toUpperCase() + selectedRole.slice(1)} successfully`);
      setShowChangeRoleModal(false);

    } catch (err) {

      toast.error(err instanceof Error ? err.message : 'Failed to change role');
    } finally {
      setChangeRoleLoading(false);
    }
  };

  // Handle change plan
  const handleChangePlan = async () => {
    if (!subscriber || !selectedPlanId) return;

    setChangePlanLoading(true);
    try {
      // Delete current subscription if it exists
      if (subscription) {
        const deleteResponse = await fetch(`${API_CONFIG.ENDPOINTS.BILLING_SUBSCRIPTION_ADMIN_DELETE}/${subscription.id}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        });

        if (!deleteResponse.ok) {
          const errorData = await deleteResponse.json();
          throw new Error(errorData.message || 'Failed to delete current subscription');
        }
      }

      // Create new subscription
      const createResponse = await fetch(API_CONFIG.ENDPOINTS.BILLING_SUBSCRIPTION_ADMIN_CREATE, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          userId: subscriber.id,
          planId: selectedPlanId
        })
      });

      if (!createResponse.ok) {
        const errorData = await createResponse.json();
        throw new Error(errorData.message || 'Failed to create new subscription');
      }

      toast.success('Plan changed successfully');

      // Refresh subscriber data
      const fetchSubscriberDetails = async () => {
        if (!id) return;

        try {
          const response = await fetch(`${API_CONFIG.ENDPOINTS.USERS_ADMIN_DETAIL}/${id}`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json'
            }
          });

          if (!response.ok) {
            throw new Error('Failed to fetch subscriber details');
          }

          const fullResponse = await response.json();

          if (fullResponse.success && fullResponse.data) {
            const { user, subscription, history } = fullResponse.data;
            setSubscriber(user);
            setSubscription(subscription);
            setHistory(history || []);
          }
        } catch (err) {

        }
      };

      await fetchSubscriberDetails();

      // Close modal and reset state
      setShowChangePlanModal(false);
      setSelectedPlanId(null);

    } catch (err) {

      toast.error(err instanceof Error ? err.message : 'Failed to change plan');
    } finally {
      setChangePlanLoading(false);
    }
  };

  // Handle remove subscription
  const handleRemoveSubscription = async () => {
    if (!subscription) return;

    try {
      setRemoveSubscriptionLoading(true);

      const response = await fetch(`${API_CONFIG.ENDPOINTS.BILLING_SUBSCRIPTION_ADMIN_DELETE}/${subscription.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to cancel subscription');
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.message || 'Failed to cancel subscription');
      }

      toast.success('Subscription removed successfully');
      setShowRemoveSubscriptionModal(false);

      // Refresh subscriber data
      const fetchSubscriberDetails = async () => {
        if (!id) return;

        try {
          const response = await fetch(`${API_CONFIG.ENDPOINTS.USERS_ADMIN_DETAIL}/${id}`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json'
            }
          });

          if (!response.ok) {
            throw new Error('Failed to fetch subscriber details');
          }

          const fullResponse = await response.json();

          if (fullResponse.success && fullResponse.data) {
            const { user, subscription, history } = fullResponse.data;
            setSubscriber(user);
            setSubscription(subscription);
            setHistory(history || []);
          }
        } catch (err) {

        }
      };

      fetchSubscriberDetails();

    } catch (err) {

      toast.error(err instanceof Error ? err.message : 'Failed to Cancel subscription');
    } finally {
      setRemoveSubscriptionLoading(false);
    }
  };

  // Handle add days
  const handleAddDays = async () => {
    if (!subscriber || !addDaysForm.days) return;

    const days = parseInt(addDaysForm.days);
    if (isNaN(days) || days <= 0 || days > 365) {
      toast.error('Please enter a valid number of days (1-365)');
      return;
    }

    // Validate plan selection for free users
    if (!subscription && !addDaysForm.planIdForFree) {
      toast.error('Please select a plan for the new subscription');
      return;
    }

    try {
      setAddDaysLoading(true);

      const response = await fetch(`${API_CONFIG.ENDPOINTS.USERS_ADMIN_ADD_DAYS}/${subscriber.id}/subscription/add-days`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          days: days,
          planIdForFree: addDaysForm.planIdForFree || undefined,
          reason: addDaysForm.reason || undefined
        })
      });

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.message || 'Failed to add days');
      }

      toast.success(`Successfully added ${days} days${result.data.wasCreated ? ' (created new subscription)' : ''}`);

      // Close modal and reset form
      setShowAddDaysModal(false);
      setAddDaysForm({ days: '', planIdForFree: '', reason: '' });

      // Refresh subscriber data
      const fetchSubscriberDetails = async () => {
        if (!id) return;

        try {
          const response = await fetch(`${API_CONFIG.ENDPOINTS.USERS_ADMIN_DETAIL}/${id}`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json'
            }
          });

          if (!response.ok) {
            throw new Error('Failed to fetch subscriber details');
          }

          const fullResponse = await response.json();

          if (fullResponse.success && fullResponse.data) {
            const { user, subscription, history } = fullResponse.data;
            setSubscriber(user);
            setSubscription(subscription);
            setHistory(history || []);
          }
        } catch (err) {

        }
      };

      fetchSubscriberDetails();

    } catch (err) {

      toast.error(err instanceof Error ? err.message : 'Failed to add days');
    } finally {
      setAddDaysLoading(false);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="subscriber-detail">
        <div className="subscriber-detail__loading">
          <div className="subscriber-detail__loading-spinner"></div>
          <p>Loading subscriber details...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="subscriber-detail">
        <div className="subscriber-detail__error">
          <h2>Error Loading Subscriber</h2>
          <p>{error}</p>
          <button
            className="subscriber-detail__back-button"
            onClick={handleBackToSubscribers}
          >
            Back to Subscribers
          </button>
        </div>
      </div>
    );
  }

  // No subscriber data
  if (!subscriber) {
    return (
      <div className="subscriber-detail">
        <div className="subscriber-detail__error">
          <h2>Subscriber Not Found</h2>
          <p>The requested subscriber could not be found.</p>
          <button
            className="subscriber-detail__back-button"
            onClick={handleBackToSubscribers}
          >
            Back to Subscribers
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`subscriber-detail ${isDarkMode ? 'dark' : ''}`} data-theme={isDarkMode ? 'dark' : 'light'}>
      {/* Header with breadcrumb */}
      <header className="subscriber-detail__header">
        <nav className="subscriber-detail__breadcrumb">
          <button
            className="subscriber-detail__breadcrumb-link"
            onClick={handleBackToSubscribers}
          >
            ← Subscribers
          </button>
          <span className="subscriber-detail__breadcrumb-separator">/</span>
          <span className="subscriber-detail__breadcrumb-current">
            {subscriber.name}
          </span>
        </nav>

        <div className="subscriber-detail__header-content">
          <div className="subscriber-detail__title-section">
            <h1 className="subscriber-detail__title">{subscriber.name}</h1>
            <div className="subscriber-detail__status">
              <span className="subscriber-detail__status-indicator">
                <span className="subscriber-detail__status-dot"></span>
                <span className="subscriber-detail__status-text">Active Account</span>
              </span>
            </div>
          </div>
          <button className="subscriber-detail__send-message-btn">
            <span className="material-symbols-outlined" style={{fontSize: '1.25rem'}}>mail</span>
            <span>Send Message</span>
          </button>
        </div>
      </header>

      {/* Main content grid */}
      <main className="subscriber-detail__content">
        {/* Left column - User info and subscription */}
        <div className="subscriber-detail__left-column">
          {/* User Info Card */}
          <div className="subscriber-detail__card">
            <h3 className="subscriber-detail__card-title">User Profile</h3>
            <div className="subscriber-detail__card-content">
              <div className="subscriber-detail__user-info">
                <div className="subscriber-detail__user-avatar">
                  {subscriber.name.charAt(0).toUpperCase()}
                </div>
                <div>
                  <p className="subscriber-detail__user-name">{subscriber.name}</p>
                  <p className="subscriber-detail__user-email">{subscriber.email}</p>
                </div>
              </div>

              <div className="subscriber-detail__info-grid">
                {subscriber.phone && (
                  <div className="subscriber-detail__info-item">
                    <span className="subscriber-detail__info-label">
                      <span className="material-symbols-outlined" style={{fontSize: '1rem'}}>phone</span>
                    </span>
                    <span className="subscriber-detail__info-value">{subscriber.phone}</span>
                  </div>
                )}

                <div className="subscriber-detail__info-item">
                  <span className="subscriber-detail__info-label">
                    <span className="material-symbols-outlined" style={{fontSize: '1rem'}}>calendar_month</span>
                  </span>
                  <div style={{display: 'flex', flexDirection: 'column'}}>
                    <span style={{fontSize: '0.75rem', textTransform: 'uppercase', fontWeight: 600, opacity: 0.7}}>Sign-up</span>
                    <span className="subscriber-detail__info-value">
                      {formatDate(subscriber.createdAt)}
                    </span>
                  </div>
                </div>

                <div className="subscriber-detail__info-item">
                  <span className="subscriber-detail__info-label">
                    <span className="material-symbols-outlined" style={{fontSize: '1rem'}}>login</span>
                  </span>
                  <div style={{display: 'flex', flexDirection: 'column'}}>
                    <span style={{fontSize: '0.75rem', textTransform: 'uppercase', fontWeight: 600, opacity: 0.7}}>Last Login</span>
                    <span className="subscriber-detail__info-value">
                      {subscriber.lastLogin ? formatDate(subscriber.lastLogin) : 'Never'}
                    </span>
                  </div>
                </div>

                <div className="subscriber-detail__info-item">
                  <span className="subscriber-detail__info-label">
                    <span className="material-symbols-outlined" style={{fontSize: '1rem'}}>person</span>
                  </span>
                  <span className={`subscriber-detail__role-badge subscriber-detail__role-badge--${subscriber.role}`}>
                    {subscriber.role.charAt(0).toUpperCase() + subscriber.role.slice(1)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Subscription Info Card */}
          <div className="subscriber-detail__card">
            <h3 className="subscriber-detail__card-title">Subscription Details</h3>
            <div className="subscriber-detail__card-content">
              <div className="subscriber-detail__subscription-grid">
                <div className="subscriber-detail__subscription-item">
                  <span className="subscriber-detail__subscription-label">
                    <span className="material-symbols-outlined" style={{fontSize: '1rem'}}>card_membership</span>
                  </span>
                  <div>
                    <span style={{fontSize: '0.75rem', textTransform: 'uppercase', fontWeight: 600, opacity: 0.7, display: 'block', marginBottom: '0.25rem'}}>Current Plan</span>
                    <span className="subscriber-detail__subscription-value">
                      {(() => {
                        const planValue = subscription?.plan || subscriber.plan;
                        return planValue;
                      })()}
                    </span>
                  </div>
                </div>

                {subscription && (
                  <>
                    <div className="subscriber-detail__subscription-item">
                      <span className="subscriber-detail__subscription-label">
                        <span className="material-symbols-outlined" style={{fontSize: '1rem'}}>attach_money</span>
                      </span>
                      <div>
                        <span style={{fontSize: '0.75rem', textTransform: 'uppercase', fontWeight: 600, opacity: 0.7, display: 'block', marginBottom: '0.25rem'}}>Price</span>
                        <span className="subscriber-detail__subscription-value">
                          {formatCurrency(subscription.price, 'PHP')} / {subscription.billingCycle}
                        </span>
                      </div>
                    </div>

                    <div className="subscriber-detail__subscription-item">
                      <span className="subscriber-detail__subscription-label">
                        <span className="material-symbols-outlined" style={{fontSize: '1rem'}}>event</span>
                      </span>
                      <div>
                        <span style={{fontSize: '0.75rem', textTransform: 'uppercase', fontWeight: 600, opacity: 0.7, display: 'block', marginBottom: '0.25rem'}}>Next Payment</span>
                        <span className="subscriber-detail__subscription-value">
                          {subscription.nextPayment ? formatDate(subscription.nextPayment) : 'N/A'}
                        </span>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Right column - Admin controls and history */}
        <div className="subscriber-detail__right-column">
          {/* Admin Controls Card */}
          <div className="subscriber-detail__card">
            <h3 className="subscriber-detail__card-title">Admin Actions</h3>
            <div className="subscriber-detail__card-content">
              <div className="subscriber-detail__admin-controls">
                <button 
                  className="subscriber-detail__admin-button" 
                  onClick={() => navigate(`/panel/admin/subscriber-management/${id}/edit`)}
                  title="Edit user details"
                >
                  <span className="material-symbols-outlined subscriber-detail__admin-icon">edit</span>
                  <span>Edit Details</span>
                </button>

                <button
                  className="subscriber-detail__admin-button"
                  onClick={() => setShowResetPasswordModal(true)}
                  title="Reset user password"
                >
                  <span className="material-symbols-outlined subscriber-detail__admin-icon">lock_reset</span>
                  <span>Reset Password</span>
                </button>

                <button
                  className="subscriber-detail__admin-button"
                  onClick={() => {
                    setSelectedRole(subscriber.role as 'User' | 'Consultant' | 'Admin' | 'Enterprise');
                    setShowChangeRoleModal(true);
                  }}
                  title="Change user role"
                >
                  <span className="material-symbols-outlined subscriber-detail__admin-icon">manage_accounts</span>
                  <span>Change Role</span>
                </button>

                <button
                  className="subscriber-detail__admin-button"
                  onClick={() => setShowAddDaysModal(true)}
                  title="Add days to subscription"
                >
                  <span className="material-symbols-outlined subscriber-detail__admin-icon">schedule</span>
                  <span>Add Days</span>
                </button>

                <button
                  className="subscriber-detail__admin-button"
                  onClick={() => {
                    setShowChangePlanModal(true);
                    fetchAvailablePlans();
                  }}
                  title="Change subscription plan"
                >
                  <span className="material-symbols-outlined subscriber-detail__admin-icon">change_circle</span>
                  <span>Change Plan</span>
                </button>

                <button
                  className="subscriber-detail__admin-button subscriber-detail__admin-button--danger"
                  onClick={() => setShowRemoveSubscriptionModal(true)}
                  disabled={!subscription}
                  title="Cancel subscription"
                >
                  <span className="material-symbols-outlined subscriber-detail__admin-icon">unsubscribe</span>
                  <span>Cancel Plan</span>
                </button>

                <button
                  className="subscriber-detail__admin-button subscriber-detail__admin-button--danger"
                  onClick={() => setShowDeleteModal(true)}
                  title="Delete user account"
                >
                  <span className="material-symbols-outlined subscriber-detail__admin-icon">delete_forever</span>
                  <span>Delete Account</span>
                </button>
              </div>
            </div>
          </div>

          {/* History Card */}
          <div className="subscriber-detail__card">
            <h3 className="subscriber-detail__card-title">Activity History</h3>
            <div className="subscriber-detail__card-content">
              <div className="subscriber-detail__history">
                {history.length > 0 ? (
                  <ul className="subscriber-detail__history-list">
                    {history.map((item, index) => (
                      <li key={index} className="subscriber-detail__history-item">
                        <div className="subscriber-detail__history-icon">
                          <span className="material-symbols-outlined">{item.icon}</span>
                        </div>
                        <div className="subscriber-detail__history-content">
                          <p className="subscriber-detail__history-description">{item.description}</p>
                          <p className="subscriber-detail__history-date">
                            {formatDate(item.date)}
                          </p>
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="subscriber-detail__history-empty">No activity history available</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Delete Account Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        title="Delete Account"
        onClose={() => setShowDeleteModal(false)}
        actionLabel={isLoading ? "Deleting..." : "Delete Account"}
        onAction={handleDeleteAccount}
        actionDisabled={isLoading}
        isDanger
      >
        <div style={{ marginBottom: '0' }}>
          <p style={{ margin: '0 0 1rem 0', fontSize: '0.95rem', color: 'inherit', lineHeight: 1.6 }}>
            <strong>Are you sure you want to delete this account?</strong>
          </p>
          <p style={{ margin: '0 0 1rem 0', fontSize: '0.9rem', opacity: 0.8, lineHeight: 1.5 }}>
            This action <strong>cannot be undone</strong>. The account will be permanently deleted along with all associated data.
          </p>
          {subscriber && (
            <div style={{
              background: 'rgba(220, 38, 38, 0.08)',
              border: '1px solid rgba(220, 38, 38, 0.2)',
              borderRadius: '0.625rem',
              padding: '1rem',
              marginTop: '1rem'
            }}>
              <p style={{ margin: '0', fontSize: '0.9rem', fontWeight: 600 }}>
                {subscriber.name}
              </p>
              <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.85rem', opacity: 0.8 }}>
                {subscriber.email}
              </p>
            </div>
          )}
        </div>
      </Modal>

      {/* Reset Password Modal */}
      <Modal
        isOpen={showResetPasswordModal}
        title="Reset Password"
        onClose={() => {
          setShowResetPasswordModal(false);
          setResetPasswordForm({ password: '', confirmPassword: '' });
          setShowPasswords({ password: false, confirmPassword: false });
        }}
        actionLabel={resetPasswordLoading ? "Resetting..." : "Reset Password"}
        onAction={handleResetPassword}
        actionDisabled={resetPasswordLoading}
      >
        <div>
          <p style={{ marginBottom: '1.5rem', fontSize: '0.95rem', opacity: 0.9, lineHeight: 1.5 }}>
            Set a new password for <strong>{subscriber?.name}</strong>. The user won't need to know their old password.
          </p>

          <div style={{ marginBottom: '1.25rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, fontSize: '0.9rem' }}>
              New Password
            </label>
            <div className="password-input-container">
              <input
                type={showPasswords.password ? 'text' : 'password'}
                value={resetPasswordForm.password}
                onChange={(e) => setResetPasswordForm({ ...resetPasswordForm, password: e.target.value })}
                placeholder="Enter new password"
                required
              />
              <button
                type="button"
                onClick={() => setShowPasswords({ ...showPasswords, password: !showPasswords.password })}
                className="password-toggle"
              >
                <EyeIcon show={showPasswords.password} />
              </button>
            </div>
          </div>

          <div style={{ marginBottom: '1.25rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, fontSize: '0.9rem' }}>
              Confirm Password
            </label>
            <div className="password-input-container">
              <input
                type={showPasswords.confirmPassword ? 'text' : 'password'}
                value={resetPasswordForm.confirmPassword}
                onChange={(e) => setResetPasswordForm({ ...resetPasswordForm, confirmPassword: e.target.value })}
                placeholder="Confirm new password"
                required
              />
              <button
                type="button"
                onClick={() => setShowPasswords({ ...showPasswords, confirmPassword: !showPasswords.confirmPassword })}
                className="password-toggle"
              >
                <EyeIcon show={showPasswords.confirmPassword} />
              </button>
            </div>
          </div>

          <div style={{ 
            fontSize: '0.85rem', 
            opacity: 0.8,
            background: 'rgba(43, 212, 189, 0.05)',
            border: '1px solid rgba(43, 212, 189, 0.15)',
            borderRadius: '0.625rem',
            padding: '1rem'
          }}>
            <strong style={{ display: 'block', marginBottom: '0.5rem' }}>Password requirements:</strong>
            <ul style={{ margin: '0.5rem 0 0 1.5rem', paddingLeft: 0 }}>
              <li>At least 8 characters</li>
              <li>One uppercase letter</li>
              <li>One lowercase letter</li>
              <li>One number</li>
              <li>One special character (@$!%*?&)</li>
            </ul>
          </div>
        </div>
      </Modal>

      {/* Change Role Modal */}
      <Modal
        isOpen={showChangeRoleModal}
        title="Change User Role"
        onClose={() => setShowChangeRoleModal(false)}
        actionLabel={changeRoleLoading ? "Changing..." : "Change Role"}
        onAction={handleChangeRole}
        actionDisabled={changeRoleLoading}
      >
        <div>
          <p style={{ marginBottom: '1.75rem', fontSize: '0.95rem', opacity: 0.9, lineHeight: 1.6 }}>
            Select a new role for <strong>{subscriber?.name}</strong>.
            <br />
            Current role: <strong>{subscriber?.role.charAt(0).toUpperCase() + subscriber?.role.slice(1)}</strong>
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {[
              { value: 'User' as const, label: 'USER', description: 'REGULAR USER WITH BASIC ACCESS' },
              { value: 'Consultant' as const, label: 'CONSULTANT', description: 'ENHANCED ACCESS TO USER DATA' },
              { value: 'Admin' as const, label: 'ADMIN', description: 'FULL SYSTEM ACCESS' },
              { value: 'Enterprise' as const, label: 'ENTERPRISE', description: 'ORGANIZATIONAL ACCESS' }
            ].map((role) => (
              <label
                key={role.value}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '1.5rem 1.25rem',
                  border: `2px solid ${selectedRole === role.value ? '#2bd4bd' : '#4b5563'}`,
                  borderRadius: '0.875rem',
                  cursor: 'pointer',
                  backgroundColor: selectedRole === role.value ? 'rgba(43, 212, 189, 0.12)' : 'transparent',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  position: 'relative'
                }}
              >
                <div style={{ flex: 1, textAlign: 'center' }}>
                  <div style={{ 
                    fontWeight: 700, 
                    fontSize: '1.05rem', 
                    marginBottom: '0.5rem',
                    letterSpacing: '0.05em',
                    color: selectedRole === role.value ? '#2bd4bd' : 'inherit'
                  }}>
                    {role.label}
                  </div>
                  <div style={{ 
                    fontSize: '0.8rem', 
                    opacity: 0.7,
                    letterSpacing: '0.04em',
                    fontWeight: 500
                  }}>
                    {role.description}
                  </div>
                </div>
                <input
                  type="radio"
                  name="role"
                  value={role.value}
                  checked={selectedRole === role.value}
                  onChange={(e) => setSelectedRole(e.target.value as 'User' | 'Consultant' | 'Admin' | 'Enterprise')}
                  style={{ 
                    marginLeft: '1rem',
                    cursor: 'pointer',
                    width: '20px',
                    height: '20px',
                    flexShrink: 0
                  }}
                />
              </label>
            ))}
          </div>
        </div>
      </Modal>

      {/* Change Plan Modal */}
      <Modal
        isOpen={showChangePlanModal}
        title="Change Subscription Plan"
        onClose={() => {
          setShowChangePlanModal(false);
          setSelectedPlanId(null);
        }}
        actionLabel={changePlanLoading ? "Changing Plan..." : "Change Plan"}
        onAction={handleChangePlan}
        actionDisabled={changePlanLoading || !selectedPlanId}
      >
        <div style={{ maxHeight: '65vh', overflowY: 'auto', paddingRight: '0.5rem' }}>
          <p style={{ marginBottom: '1.5rem', fontSize: '0.95rem', textAlign: 'center', opacity: 0.9, lineHeight: 1.5 }}>
            Select a new plan for <strong>{subscriber?.name}</strong>.
            {subscription && (
              <div style={{ marginTop: '0.5rem', fontSize: '0.9rem' }}>Current: <strong>{subscription.plan}</strong></div>
            )}
          </p>

          {availablePlans.length > 0 ? (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
              gap: '1rem',
              marginBottom: '1.5rem'
            }}>
              {availablePlans.map((plan) => {
                const isCurrentPlan = subscription && plan.name === subscription.plan;
                return (
                <div
                  key={plan._id}
                  onClick={() => setSelectedPlanId(plan._id)}
                  style={{
                    border: `1.5px solid ${selectedPlanId === plan._id ? '#2bd4bd' : isCurrentPlan ? '#38b6a6' : '#e5e7eb'}`,
                    borderRadius: '0.75rem',
                    padding: '1.25rem',
                    cursor: 'pointer',
                    backgroundColor: selectedPlanId === plan._id ? 'rgba(43, 212, 189, 0.08)' : isCurrentPlan ? 'rgba(56, 182, 166, 0.05)' : 'transparent',
                    transition: 'all 0.3s ease',
                    position: 'relative',
                    opacity: isCurrentPlan ? 1 : 0.9
                  }}
                >
                  {isCurrentPlan && (
                    <div style={{
                      position: 'absolute',
                      top: '0.75rem',
                      left: '1.25rem',
                      backgroundColor: '#38b6a6',
                      color: 'white',
                      padding: '0.25rem 0.75rem',
                      borderRadius: '0.375rem',
                      fontSize: '0.7rem',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}>
                      Current Plan
                    </div>
                  )}
                  <div style={{ marginBottom: '0.75rem', marginTop: isCurrentPlan ? '1.75rem' : '0' }}>
                    <h3 style={{
                      fontSize: '1.1rem',
                      fontWeight: 700,
                      margin: '0 0 0.5rem 0'
                    }}>
                      {plan.name}
                    </h3>
                    <div style={{ marginBottom: '0.5rem' }}>
                      {plan.price && plan.price > 0 && plan.price < 99999 ? (
                        <>
                          <span style={{
                            fontSize: '1.35rem',
                            fontWeight: 700
                          }}>
                            {formatCurrency(plan.price, plan.currency || 'PHP')}
                          </span>
                          <span style={{
                            fontSize: '0.85rem',
                            opacity: 0.7,
                            marginLeft: '0.25rem'
                          }}>
                            /{formatBillingInterval(plan.billing_interval || 'month')}
                          </span>
                        </>
                      ) : (
                        <span style={{
                          fontSize: '1.1rem',
                          fontWeight: 700
                        }}>
                          Contact us
                        </span>
                      )}
                    </div>
                    <p style={{
                      opacity: 0.8,
                      fontSize: '0.85rem',
                      margin: '0.5rem 0 0.75rem 0',
                      lineHeight: 1.4
                    }}>
                      {plan.description}
                    </p>
                  </div>

                  {generatePlanFeatures(plan).length > 0 && (
                    <div style={{ marginBottom: '0.75rem' }}>
                      <h4 style={{
                        fontSize: '0.8rem',
                        fontWeight: 600,
                        margin: '0 0 0.5rem 0',
                        textTransform: 'uppercase',
                        opacity: 0.7
                      }}>
                        Key Features
                      </h4>
                      <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                        {generatePlanFeatures(plan).slice(0, 2).map((feature, index) => (
                          <li key={index} style={{
                            display: 'flex',
                            alignItems: 'center',
                            fontSize: '0.85rem',
                            marginBottom: '0.25rem',
                            opacity: 0.85
                          }}>
                            <span style={{
                              color: '#38e07b',
                              marginRight: '0.5rem',
                              fontSize: '0.75rem',
                              fontWeight: 700
                            }}>
                              ✓
                            </span>
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div style={{
                    position: 'absolute',
                    top: '1rem',
                    right: '1rem',
                    width: '1.25rem',
                    height: '1.25rem',
                    borderRadius: '50%',
                    border: `2px solid ${selectedPlanId === plan._id ? '#2bd4bd' : '#d1d5db'}`,
                    backgroundColor: selectedPlanId === plan._id ? '#2bd4bd' : 'transparent',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    {selectedPlanId === plan._id && (
                      <span style={{
                        color: 'white',
                        fontSize: '0.75rem',
                        fontWeight: 'bold'
                      }}>
                        ✓
                      </span>
                    )}
                  </div>
                </div>
              );
              })}
            </div>
          ) : (
            <div style={{
              textAlign: 'center',
              padding: '2rem',
              opacity: 0.7
            }}>
              Loading available plans...
            </div>
          )}
        </div>
      </Modal>

      {/* Remove Subscription Confirmation Modal */}
      <Modal
        isOpen={showRemoveSubscriptionModal}
        title="Cancel Subscription"
        onClose={() => setShowRemoveSubscriptionModal(false)}
        actionLabel={removeSubscriptionLoading ? "Cancelling..." : "Cancel Subscription"}
        onAction={handleRemoveSubscription}
        actionDisabled={removeSubscriptionLoading}
        isDanger
      >
        <div>
          <p style={{ marginBottom: '1rem', fontSize: '0.95rem', fontWeight: 600, color: 'inherit', lineHeight: 1.6 }}>
            Are you sure you want to cancel this subscription?
          </p>
          <p style={{ margin: '0 0 1rem 0', fontSize: '0.9rem', opacity: 0.8, lineHeight: 1.5 }}>
            This will permanently remove the subscription and all associated usage records. The user will lose access to their current plan and features.
          </p>
          {subscription && (
            <div style={{
              background: 'rgba(220, 38, 38, 0.08)',
              border: '1px solid rgba(220, 38, 38, 0.2)',
              borderRadius: '0.625rem',
              padding: '1rem',
              marginTop: '1rem'
            }}>
              <p style={{ margin: '0', fontSize: '0.9rem', fontWeight: 600 }}>
                {subscription.plan}
              </p>
              <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.85rem', opacity: 0.8 }}>
                Status: {subscription.status}
              </p>
            </div>
          )}
        </div>
      </Modal>

      {/* Add Days Modal */}
      <Modal
        isOpen={showAddDaysModal}
        title="Add Days to Subscription"
        onClose={() => {
          setShowAddDaysModal(false);
          setAddDaysForm({ days: '', planIdForFree: '', reason: '' });
        }}
        actionLabel={addDaysLoading ? "Adding Days..." : "Add Days"}
        onAction={handleAddDays}
        actionDisabled={addDaysLoading || !addDaysForm.days}
      >
        <div className="add-days-modal__content">
          <p style={{ marginBottom: '1.5rem', fontSize: '0.95rem', opacity: 0.9, lineHeight: 1.5 }}>
            Add days to <strong>{subscriber?.name}</strong>'s subscription.
            {!subscription && <span style={{ color: '#dc2626', fontWeight: 600, display: 'block', marginTop: '0.5rem' }}>This will create a new subscription.</span>}
          </p>

          <div style={{ marginBottom: '1.25rem' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '0.5rem', 
              fontWeight: 600,
              fontSize: '0.9rem'
            }}>
              Days to Add *
            </label>
            <input
              type="number"
              min="1"
              max="365"
              value={addDaysForm.days}
              onChange={(e) => setAddDaysForm({ ...addDaysForm, days: e.target.value })}
              placeholder="Enter number of days"
              required
              className="add-days-modal__input"
            />
          </div>

          {!subscription && (
            <div style={{ marginBottom: '1.25rem' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '0.5rem', 
                fontWeight: 600,
                fontSize: '0.9rem'
              }}>
                Plan for New Subscription *
              </label>
              <select
                value={addDaysForm.planIdForFree}
                onChange={(e) => setAddDaysForm({ ...addDaysForm, planIdForFree: e.target.value })}
                required
                className="add-days-modal__select"
              >
                <option value="">Select a plan...</option>
                {availablePlans.map((plan) => (
                  <option key={plan._id} value={plan._id}>
                    {plan.name} - {plan.currency} {plan.price?.toFixed(2) || 'Contact us'}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '0.5rem', 
              fontWeight: 600,
              fontSize: '0.9rem'
            }}>
              Reason (Optional)
            </label>
            <textarea
              value={addDaysForm.reason}
              onChange={(e) => setAddDaysForm({ ...addDaysForm, reason: e.target.value })}
              placeholder="Why are you adding these days?"
              rows={3}
              className="add-days-modal__textarea"
            />
          </div>

          {subscription && (
            <div style={{
              background: 'rgba(43, 212, 189, 0.08)',
              border: '1px solid rgba(43, 212, 189, 0.2)',
              borderRadius: '0.625rem',
              padding: '1rem'
            }}>
              <p style={{ margin: '0', fontSize: '0.9rem', fontWeight: 600 }}>
                Current Plan: {subscription.plan}
              </p>
              <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.85rem', opacity: 0.8 }}>
                Status: {subscription.status} | End date: {subscription.nextPayment ? formatDate(subscription.nextPayment) : 'N/A'}
              </p>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default SubscriberDetailPage;
